import { motion } from "framer-motion";

/**
 * "Why Choose Us" grid of icon + label + description.
 * items: [{ icon: LucideIcon, title, description }]
 */
export const WhyChooseUs = ({
  eyebrow = "Why Choose Us",
  heading,
  subheading,
  items = [],
  background = "white",
  columns = 3,
}) => {
  const bgClass = background === "navy" ? "bg-navy" : background === "slate" ? "bg-slate-50" : "bg-white";
  const isDark = background === "navy";

  const colClass = columns === 2 ? "md:grid-cols-2" : "md:grid-cols-3";

  return (
    <section
      className={`py-20 md:py-28 ${bgClass}`}
      data-testid="why-choose-section"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14 max-w-2xl mx-auto"
        >
          <span className="text-teal font-semibold text-sm uppercase tracking-wider">
            {eyebrow}
          </span>
          <h2
            className={`font-heading text-3xl md:text-5xl font-semibold mt-3 mb-4 ${
              isDark ? "text-white" : "text-navy"
            }`}
          >
            {heading}
          </h2>
          {subheading && (
            <p
              className={`text-lg leading-relaxed ${
                isDark ? "text-slate-300" : "text-slate-600"
              }`}
            >
              {subheading}
            </p>
          )}
        </motion.div>

        <div className={`grid sm:grid-cols-2 ${colClass} gap-6`}>
          {items.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className={`rounded-2xl p-7 ${
                  isDark
                    ? "bg-white/5 backdrop-blur border border-white/10"
                    : background === "slate"
                    ? "bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                    : "bg-slate-50 hover:bg-navy hover:text-white transition-colors duration-300 group"
                }`}
                data-testid={`why-choose-item-${i}`}
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${
                    isDark
                      ? "bg-teal/10"
                      : "bg-teal/10 group-hover:bg-teal/20"
                  }`}
                >
                  {Icon && <Icon className="w-6 h-6 text-teal" />}
                </div>
                <h3
                  className={`font-heading text-lg font-semibold mb-2 ${
                    isDark ? "text-white" : "text-navy group-hover:text-white"
                  } transition-colors`}
                >
                  {item.title}
                </h3>
                <p
                  className={`text-sm leading-relaxed ${
                    isDark
                      ? "text-slate-300"
                      : "text-slate-600 group-hover:text-slate-300"
                  } transition-colors`}
                >
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
